/**
 * The session store: a subscription around the reducer, and nothing else.
 *
 * No state-management library. `useSyncExternalStore` is in React itself and is precisely
 * the API for this — an external mutable source read through a snapshot — and a reducer
 * this small does not need middleware, devtools plumbing or a context generator to be
 * legible.
 *
 * The store's only two jobs beyond `state = reducer(state, action)`:
 *
 *  - **Ordering.** A listener that dispatches (the effects do, constantly: a chord lands,
 *    branches are requested) must not re-enter the reducer mid-notification. Actions
 *    dispatched from a listener are queued and applied after the current notification
 *    finishes, so every subscriber sees every state in the same order.
 *  - **Handing listeners the previous state.** Effects are written as diffs — "the current
 *    node changed", "the revision changed" — and computing those from the state alone
 *    would mean every effect keeping its own shadow copy.
 *
 * Side effects live in `effects.ts`, attached from outside. This file never fetches,
 * never writes to storage and never reads a clock except to mint a session id.
 */

import { useCallback, useRef, useSyncExternalStore } from 'react';
import {
  createInitialSessionState,
  sessionReducer,
  type Perspective,
  type SessionAction,
  type SessionReducer,
  type SessionState,
} from '@hc/shared';

export type SessionStoreListener = (
  state: SessionState,
  action: SessionAction,
  previous: SessionState,
) => void;

export interface SessionStore {
  getState(): SessionState;
  dispatch(action: SessionAction): void;
  /** Returns an unsubscribe function. */
  subscribe(listener: SessionStoreListener): () => void;
}

export interface CreateSessionStoreOptions {
  initialState?: SessionState;
  sessionId?: string;
  startedAt?: number;
  /** Overridable so a replay harness can supply a reducer with a different arpeggio window. */
  reducer?: SessionReducer;
}

/** A session id. Unlike everything in the reducer, this one is genuinely allowed to be random. */
export function newSessionId(): string {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * No lens, until the player picks one.
 *
 * `@hc/shared` defaults to `['pop', 'jazz']`, which is a reasonable *illustration* of the
 * field and a bad thing to actually run with: a fresh session opened those two chips
 * already lit, and every branch request went out biased toward two styles nobody had
 * asked for. Passed explicitly here rather than changed there, because the shared default
 * is frozen contract and the fixtures that name it are describing a session that chose
 * those lenses. Empty means "no bias", which is what a first chord deserves.
 */
export const LIVE_DEFAULT_PERSPECTIVES: Perspective[] = [];

export function createSessionStore(options: CreateSessionStoreOptions = {}): SessionStore {
  const reduce = options.reducer ?? sessionReducer;
  let state =
    options.initialState ??
    createInitialSessionState({
      sessionId: options.sessionId ?? newSessionId(),
      startedAt: options.startedAt ?? Date.now(),
      perspectives: [...LIVE_DEFAULT_PERSPECTIVES],
    });

  const listeners = new Set<SessionStoreListener>();
  const queue: SessionAction[] = [];
  let notifying = false;

  function dispatch(action: SessionAction): void {
    if (notifying) {
      // A listener dispatched. Finish this round first — see the file header.
      queue.push(action);
      return;
    }

    let current = action;
    notifying = true;
    try {
      for (;;) {
        const previous = state;
        state = reduce(previous, current);
        if (state !== previous) {
          for (const listener of [...listeners]) listener(state, current, previous);
        }
        const next = queue.shift();
        if (!next) break;
        current = next;
      }
    } finally {
      notifying = false;
      queue.length = 0;
    }
  }

  return {
    getState: () => state,
    dispatch,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// ---------------------------------------------------------------------------
// The application's store
// ---------------------------------------------------------------------------

let defaultStore: SessionStore | null = null;

/** The store the running app uses. Created on first access so tests can pre-empt it. */
export function getSessionStore(): SessionStore {
  if (!defaultStore) defaultStore = createSessionStore();
  return defaultStore;
}

/** Install a store — at startup, and in tests. Passing `null` forgets the current one. */
export function setSessionStore(store: SessionStore | null): void {
  defaultStore = store;
}

// ---------------------------------------------------------------------------
// React binding
// ---------------------------------------------------------------------------

/**
 * Subscribe a component to the whole session state.
 *
 * `SessionState` is replaced wholesale on every change, so a component that only cares
 * about part of it should use `useSessionSelector` and let `Object.is` do the filtering.
 */
export function useSessionState(store: SessionStore = getSessionStore()): SessionState {
  const subscribe = useCallback(
    (onChange: () => void) => store.subscribe(() => onChange()),
    [store],
  );
  const getSnapshot = useCallback(() => store.getState(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Subscribe to a derived value.
 *
 * The result is cached against the state object it was derived from, because
 * `useSyncExternalStore` requires `getSnapshot` to be stable between changes — a selector
 * returning a fresh array each call would otherwise loop forever.
 */
export function useSessionSelector<T>(
  selector: (state: SessionState) => T,
  store: SessionStore = getSessionStore(),
): T {
  const cache = useRef<{ state: SessionState; value: T } | null>(null);
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const subscribe = useCallback(
    (onChange: () => void) => store.subscribe(() => onChange()),
    [store],
  );
  const getSnapshot = useCallback(() => {
    const state = store.getState();
    if (!cache.current || cache.current.state !== state) {
      cache.current = { state, value: selectorRef.current(state) };
    }
    return cache.current.value;
  }, [store]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
